import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge } from '@/backoffice/components/ui';
import { LocalizationInputGroup, type LocalizationValue } from '@/backoffice/components/forms/LocalizationInputGroup';
import { formatCurrency } from '@/backoffice/utils';
import { fetchDriverZones, type DriverZonesData } from '@/domains/driver/services/driver.service';
import { getSession } from '@/domains/auth/services/auth.service';
import { syncUserCoverageSecteurs, detachUserCoverageSecteurs } from '@/domains/api/user-relations.api';
import { MapPin, TrendingUp, Plus, X, Loader2 } from 'lucide-react';

const emptyLoc = (): LocalizationValue => ({
  countryId: null,
  regionId: null,
  prefectureId: null,
  communeId: null,
  districtId: null,
  secteurId: null,
});

export function ProZonesPage() {
  const [zonesData, setZonesData] = useState<DriverZonesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loc, setLoc] = useState<LocalizationValue>(emptyLoc());
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    fetchDriverZones()
      .then(setZonesData)
      .catch(() => setZonesData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const activeSecteurs = zonesData?.active_secteurs ?? zonesData?.active_zones ?? [];

  const handleAddSecteur = async () => {
    if (!loc.secteurId) {
      setError('Sélectionnez un secteur complet (pays → région → … → secteur).');
      return;
    }

    if (activeSecteurs.some((s) => s.id === loc.secteurId)) {
      setError('Ce secteur est déjà dans votre couverture.');
      return;
    }

    const userId = getSession()?.user?.id;
    if (!userId) return;

    setSaving(true);
    setError(null);
    try {
      await syncUserCoverageSecteurs(userId, [loc.secteurId]);
      setLoc(emptyLoc());
      load();
    } catch {
      setError('Impossible d\'ajouter ce secteur.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSecteur = async (secteurId: number) => {
    const userId = getSession()?.user?.id;
    if (!userId) return;

    setSaving(true);
    setError(null);
    try {
      await detachUserCoverageSecteurs(userId, [secteurId]);
      load();
    } catch {
      setError('Impossible de retirer ce secteur.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="pro_driver" title="Mes zones">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="pro_driver" title="Zones de couverture">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <Card className="mb-6">
        <p className="text-sm text-gray-600 mb-4">
          Les missions disponibles apparaissent lorsque le point de collecte ou de livraison se trouve dans l&apos;un de vos secteurs couverts.
        </p>
        <h3 className="font-semibold text-gray-900 mb-4">
          Secteurs actifs ({activeSecteurs.length})
        </h3>

        {activeSecteurs.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucun secteur configuré. Ajoutez votre première zone ci-dessous.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeSecteurs.map((secteur) => (
              <div key={secteur.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg relative">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-blue-900">{secteur.name}</p>
                    <p className="text-sm text-blue-700 flex items-center gap-1 mt-1">
                      <MapPin className="w-4 h-4 shrink-0" />
                      Secteur couvert
                    </p>
                  </div>
                  <button
                    type="button"
                    className="p-1 rounded-full hover:bg-blue-100 shrink-0"
                    onClick={() => handleRemoveSecteur(secteur.id)}
                    disabled={saving}
                  >
                    <X className="w-4 h-4 text-blue-600" />
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-3 text-sm">
                  <div className="flex items-center gap-1 text-blue-700">
                    <TrendingUp className="w-4 h-4" />
                    <span>{secteur.deliveries} livraisons</span>
                  </div>
                  <div className="text-blue-700">{formatCurrency(secteur.revenue)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Performance par secteur</h3>
        {activeSecteurs.length === 0 ? (
          <p className="text-gray-500 text-sm">Aucune donnée de performance.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 text-sm font-medium text-gray-500">Secteur</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-500">Livraisons</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-500">Revenus</th>
                  <th className="text-right py-3 text-sm font-medium text-gray-500">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {activeSecteurs.map((secteur) => (
                  <tr key={secteur.id}>
                    <td className="py-3 font-medium text-gray-900">{secteur.name}</td>
                    <td className="py-3 text-right">{secteur.deliveries}</td>
                    <td className="py-3 text-right font-medium text-emerald-600">{formatCurrency(secteur.revenue)}</td>
                    <td className="py-3 text-right">{secteur.rating}/5</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Ajouter un secteur de couverture</h3>
        <LocalizationInputGroup value={loc} onChange={setLoc} className="mb-4" />
        <Button
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={handleAddSecteur}
          isLoading={saving}
          disabled={!loc.secteurId}
        >
          Ajouter ce secteur
        </Button>
      </Card>
    </DashboardLayout>
  );
}
